import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { RAIL_WIDTH as DEFAULT_RAIL_WIDTH } from '@/components/Catalog/CatalogRail';

/** Grid / list toggle values — align with store + URL conventions. */
export const CATALOG_VIEW_MODES = {
  GRID: 'grid',
  LIST: 'list',
};

/** Closest vertical scroll ancestor — the page scroller’s viewport, not `documentElement` when a nested ScrollView owns scroll. */
function getInnermostVerticalScrollAncestor(fromEl) {
  if (typeof document === 'undefined' || !fromEl || fromEl.nodeType !== 1) return null;
  let el = fromEl.parentElement;
  while (el) {
    const { overflowY } = window.getComputedStyle(el);
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

function readWebCatalogScrollPortalHeight(scrollEl) {
  if (typeof window === 'undefined') return 0;
  const root = scrollEl && scrollEl.nodeType === 1 ? scrollEl : document.documentElement;
  let h = root.clientHeight || 0;
  const vv = window.visualViewport?.height;
  if (vv) h = h ? Math.min(h, vv) : vv;
  if (!h) h = window.innerHeight || 0;
  return Math.max(160, Math.floor(h));
}

// ─── Rail rows (metrics match `CatalogRail` / deck recipe rails) ───────────

const ShellRailPinnedRow = React.memo(function ShellRailPinnedRow({
  label, icon, color, active, count, colors, onPress, iconSolid, showZeroPinnedCount,
}) {
  const [hovered, setHovered] = useState(false);
  const showCount = count != null && (showZeroPinnedCount || count > 0);
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        height: 32,
        paddingHorizontal: 10,
        marginHorizontal: 4,
        borderRadius: 6,
        backgroundColor: active
          ? `${color}1F`
          : (hovered ? colors.solid_color_tertiary : 'transparent'),
        ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
      }}
    >
      <Icon name={icon} size={11} color={active ? color : colors.text_color_secondary} solid={iconSolid} />
      <Text
        size="s"
        weight={active ? 'bold' : 'semibold'}
        numberOfLines={1}
        style={{ flex: 1, color: active ? color : colors.text_color_primary }}
      >
        {label}
      </Text>
      {showCount && (
        <Text
          size="s"
          weight="semibold"
          style={{
            color: active ? color : colors.text_color_tertiary,
            fontVariant: ['tabular-nums'],
          }}
        >
          {count > 999 ? '999+' : count}
        </Text>
      )}
    </Pressable>
  );
});

const ShellRailFacetRow = React.memo(function ShellRailFacetRow({
  label, fg, rowBg, active, count, colors, onPress,
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        height: 30,
        paddingHorizontal: 8,
        marginHorizontal: 4,
        borderRadius: 6,
        backgroundColor: active
          ? rowBg
          : (hovered ? colors.solid_color_tertiary : 'transparent'),
        ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
      }}
    >
      <View
        style={{
          width: 14,
          height: 14,
          borderRadius: 3,
          alignItems: 'center',
          justifyContent: 'center',
          borderWidth: 1.5,
          borderColor: active ? fg : colors.border_color_secondary,
          backgroundColor: active ? fg : 'transparent',
        }}
      >
        {active && <Icon name="check" size={8} color="#fff" />}
      </View>
      <Text
        size="s"
        numberOfLines={1}
        style={{ flex: 1, color: active ? fg : colors.text_color_primary }}
        weight={active ? 'bold' : 'normal'}
      >
        {label}
      </Text>
      {typeof count === 'number' && (
        <Text
          size="s"
          weight="semibold"
          style={{
            color: active ? fg : colors.text_color_tertiary,
            fontVariant: ['tabular-nums'],
          }}
        >
          {count}
        </Text>
      )}
    </Pressable>
  );
});

function createShellStyles(colors, isXS, bodyGap, railWidth, noBodyPadding) {
  return StyleSheet.create({
    split: {
      flexDirection: isXS ? 'column' : 'row',
      alignItems: isXS ? 'stretch' : 'flex-start',
      paddingLeft: 0,
      paddingRight: 0,
    },
    railOuter: {
      width: railWidth,
      flexShrink: 0,
      alignSelf: 'flex-start',
      borderRightWidth: 1,
      // Sticky rail must not grow past the viewport, or categories below the fold
      // stay unreachable (inner ScrollView never gets a bounded height). Cap height
      // and let the rail ScrollView own vertical scrolling.
      ...(Platform.OS === 'web'
        ? {
            position: 'sticky',
            top: 0,
            zIndex: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }
        : {}),
    },
    railDivider: {
      height: StyleSheet.hairlineWidth,
      backgroundColor: colors.border_color_secondary,
      marginVertical: 10,
      marginHorizontal: 12,
    },
    railSectionHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 12,
      paddingBottom: 6,
    },
    railSectionHeaderLabel: {
      textTransform: 'uppercase',
      letterSpacing: 0.6,
    },
    pillsWrap: {
      width: '100%',
      paddingVertical: 8,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border_color_secondary,
    },
    pillsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      // Match the hero / toolbar / body indent (15px on XS) so the first
      // pill lines up with the headline and search input above, instead
      // of running flush against the screen edge. The scroller itself
      // still bleeds full-width, so a partial trailing pill reads as a
      // scroll affordance rather than a clip.
      paddingLeft: 15,
      paddingRight: 15,
    },
    pinnedPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 10,
      height: 28,
      borderRadius: 14,
      borderWidth: 1,
    },
    pillDivider: {
      width: StyleSheet.hairlineWidth,
      height: 20,
      marginHorizontal: 4,
    },
    facetPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 10,
      height: 28,
      borderRadius: 14,
      borderWidth: 1,
      maxWidth: 220,
    },
    body: {
      ...(isXS ? { flexGrow: 0, flexShrink: 0, width: '100%' } : { flex: 1 }),
      minWidth: 0,
      gap: bodyGap,
    },
    children: {
      paddingHorizontal: noBodyPadding ? 0 : isXS ? 15 : 20,
      gap: bodyGap,
    },
    catalogToolbar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 12,
      width: '100%',
      paddingTop: 8,
      paddingBottom: 15,
      paddingHorizontal: isXS ? 15 : 20,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border_color_secondary,
    },
    viewToggle: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      padding: 2,
      borderRadius: 6,
      borderWidth: 1,
    },
    viewToggleBtn: {
      width: 26,
      height: 22,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 4,
    },
    catalogToolbarLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 10,
      flex: 1,
      minWidth: 0,
    },
    catalogToolbarRight: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 8,
      flexShrink: 0,
    },
    catalogToolbarStacked: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 10,
    },
    catalogToolbarLeadingRow: {
      width: '100%',
      minWidth: 0,
    },
    catalogToolbarControlsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      width: '100%',
      minWidth: 0,
    },
    catalogToolbarControlsTrailing: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      flexWrap: 'wrap',
      gap: 8,
      minWidth: 0,
    },
    catalogToolbarMobile: {
      width: '100%',
      paddingTop: 4,
      paddingBottom: 12,
      paddingHorizontal: isXS ? 15 : 20,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border_color_secondary,
    },
  });
}

/**
 * Desktop left rail + XS horizontal pill strip + catalog toolbar (grid/list +
 * optional trailing controls such as sort dropdowns). Center column is
 * `children` (featured blocks, grids, lists, etc.).
 *
 * @param {Object} props
 * @param {boolean} props.isXS
 * @param {number} props.bodyGap — vertical gap between toolbar and children
 * @param {number} [props.railWidth] — defaults to `CatalogRail` `RAIL_WIDTH` (220). Tasks uses 270.
 * @param {Array<{ key: string, label: string, icon: string, color: string, active: boolean, count?: number|null, pillCount?: number, onPress: () => void, iconSolid?: boolean, showZeroPinnedCount?: boolean }>} props.pinnedItems — `count` drives the desktop rail badge (hidden when null or 0); `pillCount` overrides the XS pill number so favorites can show 0 while the rail hides; `showZeroPinnedCount` forces the rail badge at 0 (deck favorites)
 * @param {{ headerLabel: string, facets: Array<{ key: string, label: string, active: boolean, count?: number, onPress: () => void, railTint: { fg: string, rowBg: string }, pillTint: { fg: string, rowBg: string } }>, headerAction?: { label: string, onPress: () => void } | null, emptyMessage?: string }} [props.categoriesSection]
 * @param {boolean} [props.suppressMobilePills] — when true, XS layout omits the horizontal pill strip (parent renders its own)
 * @param {boolean} [props.hideRail] — when true, omits the desktop left rail (parent renders its own filters)
 * @param {{ viewMode: string, listMode: string, gridMode: string, onViewModeChange: (mode: string) => void, listA11yLabel?: string, gridA11yLabel?: string, leading?: React.ReactNode, hideViewToggle?: boolean, trailing?: React.ReactNode, mobile?: React.ReactNode }} [props.toolbar] — omit to hide toolbar; `mobile` replaces the toolbar on XS when set
 * @param {React.ReactNode} props.children
 */
function CatalogBrowseShell({
  isXS,
  bodyGap,
  railWidth = DEFAULT_RAIL_WIDTH,
  suppressMobilePills = false,
  hideRail = false,
  pinnedItems = [],
  noBodyPadding,
  categoriesSection = null,
  toolbar = null,
  children,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const splitRef = useRef(null);
  /** Measured vertical scroll “window” for the page scroller — caps sticky rail so inner scroll reaches every row. */
  const [webRailMaxHeightPx, setWebRailMaxHeightPx] = useState(null);

  useLayoutEffect(() => {
    if (Platform.OS !== 'web' || isXS) {
      setWebRailMaxHeightPx(null);
      return undefined;
    }

    let cancelled = false;
    let ro = null;
    let scrollEl = null;

    const measure = () => {
      if (cancelled || !scrollEl) return;
      setWebRailMaxHeightPx(readWebCatalogScrollPortalHeight(scrollEl));
    };

    const node = splitRef.current;
    if (node && typeof document !== 'undefined') {
      scrollEl = getInnermostVerticalScrollAncestor(node) || document.documentElement;
      measure();
      ro = new ResizeObserver(() => measure());
      ro.observe(scrollEl);
    }

    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      const node2 = splitRef.current;
      if (!node2 || node2.nodeType !== 1 || typeof document === 'undefined') return;
      const next = getInnermostVerticalScrollAncestor(node2) || document.documentElement;
      if (next !== scrollEl) {
        ro?.disconnect();
        scrollEl = next;
        ro = new ResizeObserver(() => measure());
        ro.observe(scrollEl);
      }
      measure();
    });
    const onWin = () => measure();
    window.addEventListener('resize', onWin);
    window.visualViewport?.addEventListener?.('resize', onWin);
    window.visualViewport?.addEventListener?.('scroll', onWin);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      ro?.disconnect();
      window.removeEventListener('resize', onWin);
      window.visualViewport?.removeEventListener?.('resize', onWin);
      window.visualViewport?.removeEventListener?.('scroll', onWin);
    };
  }, [isXS]);

  const styles = useMemo(
    () => createShellStyles(colors, isXS, bodyGap, railWidth, noBodyPadding),
    [colors, isXS, bodyGap, railWidth, noBodyPadding],
  );

  const listA11y = toolbar?.listA11yLabel ?? (t('pages.common.list') || 'List');
  const gridA11y = toolbar?.gridA11yLabel ?? (t('pages.common.grid') || 'Grid');

  const onViewModeChange = toolbar?.onViewModeChange;
  const viewMode = toolbar?.viewMode;
  const listMode = toolbar?.listMode;
  const gridMode = toolbar?.gridMode;
  const selectListMode = useCallback(() => {
    if (listMode != null) onViewModeChange?.(listMode);
  }, [onViewModeChange, listMode]);
  const selectGridMode = useCallback(() => {
    if (gridMode != null) onViewModeChange?.(gridMode);
  }, [onViewModeChange, gridMode]);

  const railOuterMaxHeightStyle = useMemo(() => {
    if (Platform.OS !== 'web') return null;
    if (webRailMaxHeightPx != null) return { maxHeight: webRailMaxHeightPx };
    return { maxHeight: '100dvh' };
  }, [webRailMaxHeightPx]);

  const renderRail = () => (
    <View
      style={[
        styles.railOuter,
        railOuterMaxHeightStyle,
        { borderRightColor: colors.border_color_secondary, backgroundColor: colors.solid_color_primary },
      ]}
    >
      <ScrollView
        style={
          Platform.OS === 'web'
            ? { flexGrow: 1, flexShrink: 1, minHeight: 0, width: '100%' }
            : { flex: 1 }
        }
        contentContainerStyle={{ paddingTop: 8, paddingBottom: 16 }}
        showsVerticalScrollIndicator={false}
        bounces={false}
        overScrollMode="never"
        nestedScrollEnabled
      >
        {pinnedItems.map((row) => (
          <ShellRailPinnedRow
            key={row.key}
            label={row.label}
            icon={row.icon}
            color={row.color}
            active={row.active}
            count={row.count}
            colors={colors}
            onPress={row.onPress}
            iconSolid={row.iconSolid}
            showZeroPinnedCount={row.showZeroPinnedCount}
          />
        ))}
        {categoriesSection && (categoriesSection.facets?.length > 0 || categoriesSection.emptyMessage) ? (
          <>
            <View style={styles.railDivider} />
            <View style={styles.railSectionHeader}>
              <Text size="s" weight="bold" color="secondary" style={[styles.railSectionHeaderLabel, { flex: 1, minWidth: 0 }]}>
                {categoriesSection.headerLabel}
              </Text>
              {categoriesSection.headerAction ? (
                <Pressable
                  onPress={categoriesSection.headerAction.onPress}
                  accessibilityRole="button"
                  accessibilityLabel={categoriesSection.headerAction.label}
                  style={Platform.OS === 'web' ? { cursor: 'pointer' } : undefined}
                >
                  <Text size="s" color="tertiary" weight="semibold">{categoriesSection.headerAction.label}</Text>
                </Pressable>
              ) : null}
            </View>
            {(categoriesSection.facets && categoriesSection.facets.length > 0) ? (
              categoriesSection.facets.map((facet) => (
                <ShellRailFacetRow
                  key={facet.key}
                  label={facet.label}
                  fg={facet.railTint.fg}
                  rowBg={facet.railTint.rowBg}
                  active={facet.active}
                  count={facet.count}
                  colors={colors}
                  onPress={facet.onPress}
                />
              ))
            ) : categoriesSection.emptyMessage ? (
              <View style={{ paddingHorizontal: 12, paddingBottom: 8 }}>
                <Text size="s" color="tertiary">{categoriesSection.emptyMessage}</Text>
              </View>
            ) : null}
          </>
        ) : null}
      </ScrollView>
    </View>
  );

  const renderMobilePills = () => (
    <View style={styles.pillsWrap}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.pillsRow}
      >
        {pinnedItems.map((row) => {
          const pillNum = row.pillCount !== undefined
            ? row.pillCount
            : (typeof row.count === 'number' ? row.count : null);
          return (
            <Pressable
              key={row.key}
              onPress={row.onPress}
              style={[
                styles.pinnedPill,
                {
                  backgroundColor: row.active ? `${row.color}1F` : colors.solid_color_block,
                  borderColor: row.active ? row.color : colors.border_color_secondary,
                },
                Platform.OS === 'web' && { cursor: 'pointer' },
              ]}
              accessibilityRole="button"
              accessibilityState={{ selected: row.active }}
            >
              <Icon name={row.icon} size={11} color={row.active ? row.color : colors.text_color_secondary} solid={row.iconSolid} />
              <Text size="xs" weight="bold" style={{ color: row.active ? row.color : colors.text_color_primary }}>
                {row.label}
              </Text>
              <Text size="xxs" weight="semibold" style={{
                color: row.active ? row.color : colors.text_color_tertiary,
                fontVariant: ['tabular-nums'],
              }}
              >
                {pillNum != null ? pillNum : ''}
              </Text>
            </Pressable>
          );
        })}
        {categoriesSection && categoriesSection.facets?.length > 0 ? (
          <>
            <View style={[styles.pillDivider, { backgroundColor: colors.border_color_secondary }]} />
            {categoriesSection.facets.map((facet) => (
              <Pressable
                key={`pill-${facet.key}`}
                onPress={facet.onPress}
                style={[
                  styles.facetPill,
                  {
                    backgroundColor: facet.active ? facet.pillTint.rowBg : colors.solid_color_block,
                    borderColor: facet.active ? facet.pillTint.fg : colors.border_color_secondary,
                  },
                  Platform.OS === 'web' && { cursor: 'pointer' },
                ]}
                accessibilityRole="button"
                accessibilityState={{ selected: facet.active }}
              >
                <Text size="xs" weight={facet.active ? 'bold' : 'normal'} style={{ color: facet.active ? facet.pillTint.fg : colors.text_color_primary }} numberOfLines={1}>
                  {facet.label}
                </Text>
                {typeof facet.count === 'number' ? (
                  <Text size="xxs" weight="semibold" style={{
                    color: facet.active ? facet.pillTint.fg : colors.text_color_tertiary,
                    fontVariant: ['tabular-nums'],
                  }}
                  >
                    {facet.count}
                  </Text>
                ) : null}
              </Pressable>
            ))}
          </>
        ) : null}
      </ScrollView>
    </View>
  );

  const renderViewToggle = () => (
    <View
      style={[
        styles.viewToggle,
        { backgroundColor: colors.solid_color_block, borderColor: colors.border_color_secondary },
      ]}
    >
      <Pressable
        onPress={selectListMode}
        accessibilityLabel={listA11y}
        style={[
          styles.viewToggleBtn,
          { backgroundColor: viewMode === listMode ? colors.solid_color_tertiary : 'transparent' },
          Platform.OS === 'web' && { cursor: 'pointer' },
        ]}
        accessibilityRole="button"
        accessibilityState={{ selected: viewMode === listMode }}
      >
        <Icon name="bars" size={11} color={viewMode === listMode ? colors.text_color_primary : colors.text_color_tertiary} />
      </Pressable>
      <Pressable
        onPress={selectGridMode}
        accessibilityLabel={gridA11y}
        style={[
          styles.viewToggleBtn,
          { backgroundColor: viewMode === gridMode ? colors.solid_color_tertiary : 'transparent' },
          Platform.OS === 'web' && { cursor: 'pointer' },
        ]}
        accessibilityRole="button"
        accessibilityState={{ selected: viewMode === gridMode }}
      >
        <Icon name="grid-2" size={11} color={viewMode === gridMode ? colors.text_color_primary : colors.text_color_tertiary} />
      </Pressable>
    </View>
  );

  const renderToolbar = () => {
    if (!toolbar) return null;
    const { leading, trailing, hideViewToggle } = toolbar;
    const showViewToggle = !hideViewToggle;
    if (!leading && !showViewToggle && !trailing) return null;

    if (isXS && toolbar.mobile) {
      return (
        <View style={styles.catalogToolbarMobile}>
          {toolbar.mobile}
        </View>
      );
    }

    if (isXS && leading) {
      return (
        <View style={[styles.catalogToolbar, styles.catalogToolbarStacked]}>
          <View style={styles.catalogToolbarLeadingRow}>
            {leading}
          </View>
          {(showViewToggle || trailing) ? (
            <View style={styles.catalogToolbarControlsRow}>
              {showViewToggle ? renderViewToggle() : <View />}
              {trailing ? (
                <View style={styles.catalogToolbarControlsTrailing}>
                  {trailing}
                </View>
              ) : null}
            </View>
          ) : null}
        </View>
      );
    }

    return (
      <View style={styles.catalogToolbar}>
        {(leading || showViewToggle) ? (
          <View style={styles.catalogToolbarLeft}>
            {leading}
            {showViewToggle ? renderViewToggle() : null}
          </View>
        ) : null}
        {trailing ? (
          <View style={styles.catalogToolbarRight}>
            {trailing}
          </View>
        ) : null}
      </View>
    );
  };

  return (
    <View ref={splitRef} style={styles.split}>
      {!isXS && !hideRail && renderRail()}
      {isXS && !suppressMobilePills && renderMobilePills()}
      <View style={styles.body}>
        {renderToolbar()}
        <View style={styles.children}>
          {children}
        </View>
      </View>
    </View>
  );
}

export default React.memo(CatalogBrowseShell);
